import { cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Divider } from "../divider";
import { Icon, type IconName } from "../icon";

type FormProps =
  | ({ as?: "form"; children?: React.ReactNode } & Omit<
      React.ComponentPropsWithoutRef<"form">,
      "children" | "className"
    >)
  | ({ as: "div"; children?: React.ReactNode } & Omit<
      React.ComponentPropsWithoutRef<"div">,
      "children" | "className"
    >);

const formContainerStyles = cn(
  "border",
  "border-outline-subtle",
  "rounded-(--radius-sm)",
  "flex",
  "flex-col",
);

/**
 * Form container for related sections. Renders as `<form>` by default and
 * inserts a `<Divider>` between each direct section child.
 *
 * When rendered as `<form>`, provide `aria-label` or `aria-labelledby`
 * for accessible naming.
 *
 * Add a single `<FormFooter>` as a direct child for optional form-level status
 * and actions.
 *
 * @example
 * <Form aria-label="Notification settings">
 *   <FormSection
 *     variant="main"
 *     title="Notifications"
 *     description="Choose how alerts are delivered."
 *   />
 *   <FormFooter
 *     leading={<span># unsaved changes</span>}
 *     actions={<Button type="submit" size="sm">Save changes</Button>}
 *   />
 * </Form>
 */
function Form({ as = "form", children, ...props }: FormProps) {
  const formChildren = React.Children.toArray(children);
  const footer = formChildren.find(isFormFooterChild);
  const sectionChildren = formChildren.filter(
    (child) => !isFormFooterChild(child),
  );

  const body = (
    <>
      <div className="overflow-hidden p-(--spacing-20)">
        <div className="flex flex-col gap-(--spacing-20) w-full">
          {React.Children.map(sectionChildren, (section, i) => (
            <>
              {section}
              {i < sectionChildren.length - 1 && (
                <Divider variant="middle-inset" />
              )}
            </>
          ))}
        </div>
      </div>
      {footer}
    </>
  );

  if (as === "div") {
    return (
      <div
        className={formContainerStyles}
        {...(props as React.ComponentPropsWithoutRef<"div">)}
      >
        {body}
      </div>
    );
  }

  return (
    <form
      className={formContainerStyles}
      {...(props as React.ComponentPropsWithoutRef<"form">)}
    >
      {body}
    </form>
  );
}

function isFormFooterChild(child: React.ReactNode) {
  return React.isValidElement(child) && child.type === FormFooter;
}

type FormSectionProps = {
  variant: "main" | "subsection";
  title: React.ReactNode;
  description?: React.ReactNode;
  titleAccessory?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
};

const titleVariants = cva(
  [
    "font-(family-name:--font-family-title)",
    "font-(number:--font-weight-medium)",
    "text-text-default",
    "tracking-(--font-letter-spacing-default)",
    "shrink-0",
  ],
  {
    variants: {
      variant: {
        main: [
          "text-(length:--font-size-title-xl)",
          "leading-(--font-line-height-28)",
        ],
        subsection: [
          "text-(length:--font-size-title-md)",
          "leading-(--font-line-height-24)",
        ],
      },
    },
  },
);

const descriptionStyles = cva([
  "font-(family-name:--font-family-body)",
  "font-(number:--font-weight-medium)",
  "text-text-subtext",
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
]);

/**
 * Form section with a heading, optional description, actions, and grouped controls.
 * Main sections render an `<h2>`; subsections render an `<h3>`.
 */
function FormSection({
  variant,
  title,
  description,
  titleAccessory,
  actions,
  children,
}: FormSectionProps) {
  const Heading = variant === "main" ? "h2" : "h3";
  const id = React.useId();
  const hasDescription = description != null;

  return (
    <fieldset
      aria-describedby={hasDescription ? `${id}-description` : undefined}
      className="border-0 p-0 m-0 min-w-0"
    >
      <legend className="sr-only">{title}</legend>
      <div className="flex items-start gap-(--spacing-12)">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-(--spacing-6)">
            <Heading className={titleVariants({ variant })}>{title}</Heading>
            {titleAccessory != null && titleAccessory}
          </div>
          {hasDescription && (
            <p
              id={`${id}-description`}
              className={cn(descriptionStyles(), "mt-(--spacing-6)")}
            >
              {description}
            </p>
          )}
        </div>
        {actions != null && <div className="shrink-0">{actions}</div>}
      </div>
      {children != null && (
        <div className="mt-(--spacing-16) flex flex-col">{children}</div>
      )}
    </fieldset>
  );
}

type Column = { label: string; icon?: IconName };

type FormActionColumnHeaderProps = {
  columns: [Column] | [Column, Column];
};

const columnLabelStyles = cn(
  "font-(family-name:--font-family-label)",
  "font-(number:--font-weight-semibold)",
  "leading-(--font-line-height-20)",
  "text-text-default",
  "text-(length:--font-size-label-lg)",
  "tracking-(--font-letter-spacing-default)",
  "whitespace-nowrap",
  "shrink-0",
);

/**
 * Column labels for action columns in a form section header.
 * Accepts one or two columns, each with a required label and optional icon.
 *
 * Intended to be passed as the `actions` prop of `FormSection` to align column
 * labels over the corresponding controls in each row below.
 *
 * @example
 * <FormActionColumnHeader
 *   columns={[
 *     { label: "SMS", icon: "message" },
 *     { label: "Email", icon: "email-envelope" },
 *   ]}
 * />
 */
function FormActionColumnHeader({ columns }: FormActionColumnHeaderProps) {
  const [firstColumn, secondColumn] = columns;

  return (
    <div
      className={cn(
        "flex w-[212px] items-start p-0",
        columns.length === 2 ? "justify-between" : "justify-center",
      )}
    >
      <FormActionColumnHeaderItem column={firstColumn} />
      {secondColumn != null && (
        <FormActionColumnHeaderItem column={secondColumn} />
      )}
    </div>
  );
}

function FormActionColumnHeaderItem({ column }: { column: Column }) {
  return (
    <div className="flex flex-1 items-center justify-end gap-(--spacing-8) min-w-0 overflow-hidden">
      {column.icon != null && <Icon name={column.icon} size="sm" aria-hidden />}
      <span className={columnLabelStyles}>{column.label}</span>
    </div>
  );
}

interface FormFooterProps
  extends Omit<React.ComponentPropsWithoutRef<"footer">, "className" | "role"> {
  /** Content aligned to the start of the footer, typically status or summary text. */
  leading?: React.ReactNode;
  /** Action controls aligned to the end of the footer, typically buttons. */
  actions?: React.ReactNode;
}

/**
 * Sticky footer for form-level status and actions.
 * Place as a direct child of `Form` after the form sections.
 *
 * @example
 * <Form aria-label="Asset settings">
 *   <FormSection variant="main" title="Details" />
 *   <FormFooter
 *     leading={<span># unsaved changes</span>}
 *     actions={
 *       <>
 *         <Button type="button" variant="outline" size="sm">Cancel</Button>
 *         <Button type="submit" size="sm">Save changes</Button>
 *       </>
 *     }
 *   />
 * </Form>
 */
function FormFooter({ leading, actions, ...props }: FormFooterProps) {
  return (
    <footer role="none" className="sticky bottom-0 flex flex-col" {...props}>
      <div
        aria-hidden="true"
        className="pointer-events-none h-[58px] w-full shrink-0 bg-gradient-to-b from-surface-0 to-surface mb-[-12px]"
      />
      <div
        className={cn(
          "flex flex-col items-stretch gap-(--spacing-16) overflow-hidden border-t border-outline-subtle bg-surface p-(--spacing-16) shrink-0 rounded-b-(--radius-sm) sm:flex-row sm:items-center",
          leading != null && actions != null
            ? "sm:justify-between"
            : actions != null
              ? "sm:justify-end"
              : "sm:justify-start",
        )}
      >
        {leading != null && (
          <div className="min-w-0 w-full sm:flex-1">{leading}</div>
        )}
        {actions != null && (
          <div className="flex flex-wrap items-center justify-end gap-(--spacing-16) self-end sm:flex-nowrap sm:self-auto sm:shrink-0">
            {actions}
          </div>
        )}
      </div>
    </footer>
  );
}

export { Form, FormActionColumnHeader, FormFooter, FormSection };
export type {
  FormActionColumnHeaderProps,
  FormFooterProps,
  FormProps,
  FormSectionProps,
};
